import { LAURA_LISTING_AI_CONTEXT_MAX_CHARS } from "@/lib/constants/laura-listing-context";
import type { CatalogSuggestion } from "@/types";

export const GUEST_LAURA_DRAFT_STORAGE_KEY = "techxeed:guest-laura-draft";
export const GUEST_LAURA_DRAFT_PROCESSING_KEY = "techxeed:guest-laura-draft-processing";
export const GUEST_LAURA_DRAFT_FAILED_KEY = "techxeed:guest-laura-draft-failed";
export const GUEST_LAURA_PENDING_RESUME_KEY = "techxeed:guest-laura-pending-resume";
export const GUEST_LAURA_DRAFT_HANDOFF_SUCCESS_EVENT = "techxeed:guest-laura-draft-handoff-success";
export const OPEN_SUPPORT_CHAT_EVENT = "techxeed-open-support-chat";

export const GUEST_LAURA_DRAFT_TTL_MS = 30 * 60 * 1000;
/** Keep “in flight” long enough for OAuth return + slow API; avoids overlapping retries. */
export const GUEST_LAURA_DRAFT_PROCESSING_TTL_MS = 45 * 1000;
/** After a hard failure (CORS/403/etc.), do not auto-retry this draft for this long. */
export const GUEST_LAURA_DRAFT_FAILED_COOLDOWN_MS = 2 * 60 * 1000;
export const GUEST_LAURA_PENDING_RESUME_TTL_MS = 30 * 60 * 1000;

/**
 * CloudFront/WAF in front of api.techxeed.com blocks POSTs around ~10KB body size
 * (returns HTML 403 "Request blocked" — browser shows TypeError: Failed to fetch).
 * Keep wire payloads comfortably under that.
 */
export const LAURA_API_PAGE_CONTEXT_MAX_BYTES = 6_500;
export const LAURA_API_AI_SUMMARY_MAX_CHARS = 1_800;
export const LAURA_API_DESCRIPTION_MAX_CHARS = 1_200;
export const LAURA_API_DETAILS_MAX_CHARS = 1_200;

export type GuestLauraPendingResume = {
	chatId?: string;
	createdAt: number;
};

export type GuestLauraPageContext = {
	type: "service" | "category";
	title: string;
	description?: string;
	url?: string;
	details?: string;
	aiContextSummary?: string;
	categoryTitle?: string;
	sectorTitles?: string[];
	listingRole?: "parent" | "child";
	parentListingTitle?: string;
	excerpt?: string;
	packages?: string[];
	addons?: string[];
	links?: string[];
	filters?: string[];
	highlights?: string[];
};

export type StoredGuestLauraDraft = {
	message?: string;
	catalogSuggestions?: CatalogSuggestion[];
	pageContext?: GuestLauraPageContext;
	returnUrl?: string;
	createdAt?: string;
};

export function getStoredGuestLauraDraft(): StoredGuestLauraDraft | null {
	if (typeof window === "undefined") return null;

	try {
		const raw =
			sessionStorage.getItem(GUEST_LAURA_DRAFT_STORAGE_KEY) ??
			localStorage.getItem(GUEST_LAURA_DRAFT_STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as StoredGuestLauraDraft;
		if (!parsed || typeof parsed !== "object") return null;
		const createdAtMs = parsed.createdAt ? new Date(parsed.createdAt).getTime() : Date.now();
		if (Date.now() - createdAtMs > GUEST_LAURA_DRAFT_TTL_MS) {
			removeStoredGuestLauraDraft();
			return null;
		}
		return parsed;
	} catch {
		removeStoredGuestLauraDraft();
		return null;
	}
}

export function setStoredGuestLauraDraft(draft: StoredGuestLauraDraft) {
	if (typeof window === "undefined") return;
	const value = JSON.stringify(draft);
	sessionStorage.setItem(GUEST_LAURA_DRAFT_STORAGE_KEY, value);
	localStorage.setItem(GUEST_LAURA_DRAFT_STORAGE_KEY, value);
}

export function removeStoredGuestLauraDraft() {
	if (typeof window === "undefined") return;
	sessionStorage.removeItem(GUEST_LAURA_DRAFT_STORAGE_KEY);
	localStorage.removeItem(GUEST_LAURA_DRAFT_STORAGE_KEY);
	sessionStorage.removeItem(GUEST_LAURA_DRAFT_PROCESSING_KEY);
	localStorage.removeItem(GUEST_LAURA_DRAFT_PROCESSING_KEY);
	sessionStorage.removeItem(GUEST_LAURA_DRAFT_FAILED_KEY);
	localStorage.removeItem(GUEST_LAURA_DRAFT_FAILED_KEY);
}

/** Auth callback pages have no chat FAB — wait until returnUrl so handoff UI can resume. */
export function isGuestLauraHandoffBlockedPath(pathname?: string | null) {
	const path = (pathname ?? (typeof window !== "undefined" ? window.location.pathname : "")).toLowerCase();
	if (!path) return true;
	return (
		path.startsWith("/signup/sso") ||
		path.startsWith("/login/sso") ||
		path.startsWith("/signup/email/verification") ||
		path.startsWith("/signup/email/verification-request") ||
		path.startsWith("/signup/email/verification-complete") ||
		path.includes("/api/auth")
	);
}

export function clearGuestLauraDraftProcessing() {
	if (typeof window === "undefined") return;
	sessionStorage.removeItem(GUEST_LAURA_DRAFT_PROCESSING_KEY);
	localStorage.removeItem(GUEST_LAURA_DRAFT_PROCESSING_KEY);
}

export function setGuestLauraPendingResume(resume: { chatId?: string }) {
	if (typeof window === "undefined") return;
	const value = JSON.stringify({
		chatId: resume.chatId,
		createdAt: Date.now(),
	} satisfies GuestLauraPendingResume);
	sessionStorage.setItem(GUEST_LAURA_PENDING_RESUME_KEY, value);
	localStorage.setItem(GUEST_LAURA_PENDING_RESUME_KEY, value);
}

export function getGuestLauraPendingResume(): GuestLauraPendingResume | null {
	if (typeof window === "undefined") return null;
	try {
		const raw =
			sessionStorage.getItem(GUEST_LAURA_PENDING_RESUME_KEY) ??
			localStorage.getItem(GUEST_LAURA_PENDING_RESUME_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as GuestLauraPendingResume;
		if (!parsed?.createdAt || Date.now() - parsed.createdAt > GUEST_LAURA_PENDING_RESUME_TTL_MS) {
			clearGuestLauraPendingResume();
			return null;
		}
		return parsed;
	} catch {
		clearGuestLauraPendingResume();
		return null;
	}
}

export function clearGuestLauraPendingResume() {
	if (typeof window === "undefined") return;
	sessionStorage.removeItem(GUEST_LAURA_PENDING_RESUME_KEY);
	localStorage.removeItem(GUEST_LAURA_PENDING_RESUME_KEY);
}

export function announceGuestLauraHandoffSuccess(chatId?: string) {
	if (typeof window === "undefined") return;
	if (chatId) setGuestLauraPendingResume({ chatId });
	else setGuestLauraPendingResume({});

	window.dispatchEvent(
		new CustomEvent(GUEST_LAURA_DRAFT_HANDOFF_SUCCESS_EVENT, {
			detail: { chatId },
		}),
	);
	window.dispatchEvent(
		new CustomEvent(OPEN_SUPPORT_CHAT_EVENT, {
			detail: { source: "guest_laura_handoff", chatId },
		}),
	);
}

export function buildGuestLauraDraftFingerprint(draft: StoredGuestLauraDraft) {
	return [
		draft.createdAt,
		draft.returnUrl,
		draft.message,
		...(draft.catalogSuggestions?.map((item) => item.url) ?? []),
	]
		.filter(Boolean)
		.join("|");
}

export function isGuestLauraDraftProcessing(fingerprint: string) {
	if (typeof window === "undefined") return false;

	try {
		const raw =
			sessionStorage.getItem(GUEST_LAURA_DRAFT_PROCESSING_KEY) ??
			localStorage.getItem(GUEST_LAURA_DRAFT_PROCESSING_KEY);
		if (!raw) return false;
		const parsed = JSON.parse(raw) as { fingerprint?: string; startedAt?: number };
		if (parsed.fingerprint !== fingerprint || !parsed.startedAt) {
			sessionStorage.removeItem(GUEST_LAURA_DRAFT_PROCESSING_KEY);
			localStorage.removeItem(GUEST_LAURA_DRAFT_PROCESSING_KEY);
			return false;
		}
		if (Date.now() - parsed.startedAt > GUEST_LAURA_DRAFT_PROCESSING_TTL_MS) {
			sessionStorage.removeItem(GUEST_LAURA_DRAFT_PROCESSING_KEY);
			localStorage.removeItem(GUEST_LAURA_DRAFT_PROCESSING_KEY);
			return false;
		}
		return true;
	} catch {
		sessionStorage.removeItem(GUEST_LAURA_DRAFT_PROCESSING_KEY);
		localStorage.removeItem(GUEST_LAURA_DRAFT_PROCESSING_KEY);
		return false;
	}
}

export function setGuestLauraDraftProcessing(fingerprint: string) {
	if (typeof window === "undefined") return;
	const value = JSON.stringify({ fingerprint, startedAt: Date.now() });
	sessionStorage.setItem(GUEST_LAURA_DRAFT_PROCESSING_KEY, value);
	localStorage.setItem(GUEST_LAURA_DRAFT_PROCESSING_KEY, value);
}

export function isGuestLauraDraftFailedCooldown(fingerprint: string) {
	if (typeof window === "undefined") return false;
	try {
		const raw =
			sessionStorage.getItem(GUEST_LAURA_DRAFT_FAILED_KEY) ??
			localStorage.getItem(GUEST_LAURA_DRAFT_FAILED_KEY);
		if (!raw) return false;
		const parsed = JSON.parse(raw) as { fingerprint?: string; failedAt?: number };
		if (parsed.fingerprint !== fingerprint || !parsed.failedAt) {
			sessionStorage.removeItem(GUEST_LAURA_DRAFT_FAILED_KEY);
			localStorage.removeItem(GUEST_LAURA_DRAFT_FAILED_KEY);
			return false;
		}
		if (Date.now() - parsed.failedAt > GUEST_LAURA_DRAFT_FAILED_COOLDOWN_MS) {
			sessionStorage.removeItem(GUEST_LAURA_DRAFT_FAILED_KEY);
			localStorage.removeItem(GUEST_LAURA_DRAFT_FAILED_KEY);
			return false;
		}
		return true;
	} catch {
		sessionStorage.removeItem(GUEST_LAURA_DRAFT_FAILED_KEY);
		localStorage.removeItem(GUEST_LAURA_DRAFT_FAILED_KEY);
		return false;
	}
}

export function markGuestLauraDraftFailed(fingerprint: string) {
	if (typeof window === "undefined") return;
	const value = JSON.stringify({ fingerprint, failedAt: Date.now() });
	sessionStorage.setItem(GUEST_LAURA_DRAFT_FAILED_KEY, value);
	localStorage.setItem(GUEST_LAURA_DRAFT_FAILED_KEY, value);
	sessionStorage.removeItem(GUEST_LAURA_DRAFT_PROCESSING_KEY);
	localStorage.removeItem(GUEST_LAURA_DRAFT_PROCESSING_KEY);
}

export function clearGuestLauraDraftFailed() {
	if (typeof window === "undefined") return;
	sessionStorage.removeItem(GUEST_LAURA_DRAFT_FAILED_KEY);
	localStorage.removeItem(GUEST_LAURA_DRAFT_FAILED_KEY);
}

export function getGuestLauraDraftContent(draft: StoredGuestLauraDraft) {
	const message = typeof draft.message === "string" ? draft.message.trim() : "";
	if (message) return message;

	if (draft.catalogSuggestions?.length) {
		return "Service catalog";
	}

	return "";
}

export function withCurrentPageUrl(
	pageContext?: GuestLauraPageContext,
): GuestLauraPageContext | undefined {
	if (!pageContext) return undefined;
	const trimText = (value: string | undefined, maxLength: number) =>
		value?.replace(/\s+/g, " ").trim().slice(0, maxLength);
	const trimList = (items: string[] | undefined, maxLength: number) =>
		items?.map((item) => item.replace(/\s+/g, " ").trim().slice(0, maxLength)).filter(Boolean);
	const safePageContext: GuestLauraPageContext = {
		...pageContext,
		title: trimText(pageContext.title, 200) || pageContext.title,
		description: trimText(pageContext.description, LAURA_API_DESCRIPTION_MAX_CHARS),
		details: trimText(pageContext.details, LAURA_API_DETAILS_MAX_CHARS),
		// Full listing brief can live in local draft storage; API wire format must stay small.
		aiContextSummary: trimText(
			pageContext.aiContextSummary,
			Math.min(LAURA_LISTING_AI_CONTEXT_MAX_CHARS, LAURA_API_AI_SUMMARY_MAX_CHARS),
		),
		categoryTitle: trimText(pageContext.categoryTitle, 200),
		sectorTitles: trimList(pageContext.sectorTitles, 120)?.slice(0, 8),
		listingRole: pageContext.listingRole,
		parentListingTitle: trimText(pageContext.parentListingTitle, 200),
		excerpt: trimText(pageContext.excerpt, 200),
		packages: trimList(pageContext.packages, 200)?.slice(0, 6),
		addons: trimList(pageContext.addons, 200)?.slice(0, 6),
		links: trimList(pageContext.links, 250)?.slice(0, 6),
		filters: trimList(pageContext.filters, 80)?.slice(0, 8),
		highlights: trimList(pageContext.highlights, 120)?.slice(0, 8),
	};
	if (typeof window === "undefined") return safePageContext;

	return slimPageContextForApi({
		...safePageContext,
		url: `${window.location.pathname}${window.location.search}${window.location.hash}`,
	});
}

/** Ensure JSON body stays under CloudFront/WAF request-body inspection limits. */
export function slimPageContextForApi(
	pageContext?: GuestLauraPageContext,
): GuestLauraPageContext | undefined {
	if (!pageContext) return undefined;

	const clone: GuestLauraPageContext = { ...pageContext };
	let encoded = JSON.stringify(clone);
	if (encoded.length <= LAURA_API_PAGE_CONTEXT_MAX_BYTES) return clone;

	const shrinkFields: Array<keyof GuestLauraPageContext> = [
		"aiContextSummary",
		"details",
		"description",
		"packages",
		"addons",
		"links",
		"highlights",
		"filters",
		"sectorTitles",
		"excerpt",
	];

	for (const field of shrinkFields) {
		if (encoded.length <= LAURA_API_PAGE_CONTEXT_MAX_BYTES) break;
		const value = clone[field];
		if (typeof value === "string") {
			clone[field] = value.slice(0, Math.max(200, Math.floor(value.length / 2))) as never;
		} else if (Array.isArray(value)) {
			clone[field] = value.slice(0, Math.max(1, Math.floor(value.length / 2))) as never;
		}
		encoded = JSON.stringify(clone);
	}

	if (encoded.length > LAURA_API_PAGE_CONTEXT_MAX_BYTES) {
		return {
			type: clone.type,
			title: clone.title,
			url: clone.url,
			categoryTitle: clone.categoryTitle,
			listingRole: clone.listingRole,
			parentListingTitle: clone.parentListingTitle,
			excerpt: typeof clone.excerpt === "string" ? clone.excerpt.slice(0, 200) : undefined,
		};
	}

	return clone;
}
